/**
 * TEFAS API Client Types
 *
 * This module defines all TypeScript interfaces and types for the TEFAS client.
 * It provides strong typing for fund data, requests, and responses.
 */

/**
 * Generic API response wrapper
 */
export interface ApiResponse<T> {
  results: T[];
}

/**
 * Supported TEFAS fund types
 */
export enum FundType {
  /** Emeklilik (Pension) funds */
  EMK = 'EMK',
  /** Yatırım (Investment) funds */
  YAT = 'YAT',
  /** Bireysel Yatırım (Individual Investment) funds */
  BYF = 'BYF',
  /** Gayrimenkul Yatırım (Real Estate Investment) funds */
  GYF = 'GYF',
  /** Girişim Sermayesi Yatırım (Venture Capital Investment) funds */
  GSYF = 'GSYF'
}

/**
 * Typed response data containing fund history information
 */
export interface FundHistoryResponse {
  /** Fund identifier */
  fundCode: string;
  /** Human-readable fund name */
  fundName: string;
  /** Type of fund */
  fundType: FundType;
  /** Date of the data point (YYYY-MM-DD) */
  date: string;
  /** Fund price/NAV */
  price: number;
  /** Trading volume */
  volume: number;
  /** Total assets under management */
  assets: number;
  /** Number of investors */
  investorCount: number;
}

/**
 * Raw TEFAS API response structure
 * This represents the actual response format from the TEFAS API
 */
export interface TefasApiResponse {
  /** Response data array */
  data: TefasApiFundData[];
  /** Response metadata */
  meta?: {
    total?: number;
    page?: number;
    limit?: number;
  };
}

/**
 * Raw fund data from TEFAS API
 */
export interface TefasApiFundData {
  /** Date string (timestamp) */
  TARIH: string;
  /** Fund code */
  FONKODU: string;
  /** Fund name */
  FONUNVAN: string;
  /** Price/NAV */
  FIYAT: number;
  /** Number of shares */
  TEDPAYSAYISI: number;
  /** Number of investors */
  KISISAYISI: number;
  /** Portfolio size */
  PORTFOYBUYUKLUK: number;
  /** Exchange bulletin price */
  BORSABULTENFIYAT: string;
}

/**
 * TEFAS API request parameters
 */
export interface TefasApiRequest {
  /** Fund type */
  fontip: string;
  /** Start date */
  bastarih: string;
  /** End date */
  bittarih: string;
  /** Fund code (optional) */
  fonkod?: string;
}

/**
 * Configuration for TEFAS API client
 */
export interface TefasClientConfig {
  /** TEFAS API base URL */
  baseUrl: string;
  /** Default timeout in milliseconds */
  timeout: number;
  /** Default headers */
  headers: Record<string, string>;
  /** Maximum retry attempts */
  maxRetries: number;
  /** Retry delay in milliseconds */
  retryDelay: number;

  /**
   * Cache configuration (used when cacheAdapter is not provided)
   *
   * This configures the default in-memory cache adapter.
   * If you want to use a custom cache (Redis, Cloudflare KV, etc.),
   * provide a cacheAdapter instead.
   */
  cache?: {
    /** Whether caching is enabled (default: true) */
    enabled?: boolean;
    /** Time to live in milliseconds (default: 900000 = 15 minutes) */
    ttl?: number;
    /** Maximum number of cache entries (default: 1000) */
    maxSize?: number;
  };

  /**
   * Custom cache adapter for pluggable caching strategies
   *
   * Provide your own cache implementation (Redis, Cloudflare KV, localStorage, etc.)
   * If not provided, uses the default in-memory cache with settings from `cache` config.
   *
   * @example
   * ```typescript
   * import { RedisCacheAdapter } from '@tefas-api/cache-redis';
   *
   * const client = new TefasClient({
   *   cacheAdapter: new RedisCacheAdapter({ url: 'redis://localhost:6379' })
   * });
   * ```
   */
  cacheAdapter?: any; // CacheAdapter<ApiResponse<FundResponse>> - avoiding circular dep
}

/**
 * Date range validation result
 */
export interface DateRangeValidation {
  /** Whether date range is valid */
  isValid: boolean;
  /** Error message if invalid */
  error?: string;
  /** Parsed start date */
  startDate?: Date;
  /** Parsed end date */
  endDate?: Date;
  /** Number of days in range */
  daysInRange?: number;
}

/**
 * Result from fund search operation
 *
 * Contains basic fund identification information suitable for
 * autocomplete and selection interfaces.
 */
export interface SearchResult {
  /**
   * Fund code identifier (e.g., "IIH", "TGE")
   *
   * This is the 3-character code used for subsequent API calls
   * like getFundHistory().
   */
  fundCode: string;

  /**
   * Full fund display name including code prefix
   *
   * Format: "{CODE} - {FULL_NAME}"
   * Example: "IIH - İSTANBUL PORTFÖY ÜÇÜNCÜ HİSSE SENEDİ FONU (HİSSE SENEDİ YOĞUN FON)"
   */
  fundName: string;

  /**
   * Type of fund (e.g., FundType.EMK, FundType.YAT)
   *
   * This indicates the fund category and is always included in search results.
   */
  fundType: FundType;
}

/**
 * Optional configuration for fund search
 *
 * All properties are optional to provide flexible search capabilities.
 */
export interface SearchOptions {
  /**
   * Filter search results by fund type
   *
   * If omitted, searches across all fund types.
   *
   * @example
   * { fundType: FundType.YAT } // Only search investment funds
   */
  fundType?: FundType;

  /**
   * Maximum number of results to return
   *
   * @default 10
   * @example
   * { limit: 5 } // Return at most 5 results
   */
  limit?: number;
}

/**
 * Fund category information from Fundfy
 */
export interface FundCategory {
  abbreviation: string;
  fullName: string;
}

/**
 * Fund benchmark information from Fundfy
 */
export interface FundBenchmark {
  benchmark: string;
  percentage: number;
}

/**
 * Detailed fund information from Fundfy
 */
export interface FundDetail {
  code: string;
  title: string;
  auditFirm: string | null;
  emailAddress: string | null;
  fundDuration: string | null;
  contactAddress: string | null;
  founder: string | null;
  founderLogo: string | null;
  umbrellaFundType: string | null;
  umbrellaFundName: string | null;
  fundCategory: FundCategory | null;
  risk: number | null;
  yearlyManagementFee: number | null;
  buyingValor: number | null;
  sellingValor: number | null;
  strategyStatement: string | null;
  fundBenchmarks: FundBenchmark[];
}

/**
 * HTTP method types
 */
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

/**
 * API client configuration
 */
export interface ApiClientConfig {
  /** Base URL for API requests */
  baseUrl: string;
  /** Request timeout in milliseconds */
  timeout?: number;
  /** Default headers for all requests */
  headers?: Record<string, string>;
}

/**
 * Request options for API calls
 */
export interface RequestOptions {
  /** Query parameters */
  params?: Record<string, any>;
  /** Custom headers for this request */
  headers?: Record<string, string>;
  /** Request timeout in milliseconds */
  timeout?: number;
  /** Content type for request body */
  contentType?: 'json' | 'form';
  /** Abort signal for request cancellation */
  signal?: AbortSignal;
}

/**
 * Performance metrics for a fund
 */
export interface PerformanceMetrics {
  /** Fund identifier */
  fundCode: string;
  /** Human-readable fund name */
  fundName: string;
  /** Type of fund */
  fundType: FundType;
  /** Number of data points used in calculations */
  observations: number;
  /** Total return over the entire period */
  cumulativeReturn: number;
  /** Annualized return (yearly return) */
  annualizedReturn: number;
  /** Annualized volatility (risk measure) */
  annualizedVolatility: number;
  /** Sharpe ratio (risk-adjusted return) */
  sharpeRatio: number;
}

/**
 * Configuration options for performance metrics calculation
 */
export interface MetricsOptions {
  /** Risk-free rate for Sharpe ratio calculation (default: 0) */
  riskFreeRate?: number;
  /** Data frequency for calculations */
  frequency?: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annual';
  /** Return calculation method */
  method?: 'simple' | 'logarithmic';
  /** Minimum number of observations required (default: 30) */
  minObservations?: number;
}

/**
 * Typed response data containing fund allocation/portfolio composition information
 */
export interface FundContentResponse {
  /** Fund identifier */
  fundCode: string;
  /** Human-readable fund name */
  fundName: string;
  /** Type of fund */
  fundType: FundType;
  /** Date of the data point (YYYY-MM-DD) */
  date: string;
  /** Total fund value */
  totalValue: number;

  // Stock allocations
  /** Percentage of stocks (HS) */
  stocks?: number;
  /** Percentage of foreign securities (YYF) */
  foreignSecurities?: number;
  /** Percentage of stock-based funds (HB) */
  stockBasedFunds?: number;
  /** Percentage of stock-based investment funds (YHS) */
  stockBasedInvestmentFunds?: number;

  // Bond allocations
  /** Percentage of bonds (BB) */
  bonds?: number;
  /** Percentage of government bonds (DB) */
  governmentBonds?: number;
  /** Percentage of private sector bonds (ÖSDB) */
  privateSectorBonds?: number;
  /** Percentage of government securities (GSYKB) */
  governmentSecurities?: number;
  /** Percentage of government securities yield (GSYY) */
  governmentSecuritiesYield?: number;
  /** Percentage of government yield bonds (GYKB) */
  governmentYieldBonds?: number;
  /** Percentage of government yield (GYY) */
  governmentYield?: number;

  // Deposit and cash allocations
  /** Percentage of deposits (D) */
  deposits?: number;
  /** Percentage of reverse purchase agreements (T) */
  reversePurchaseAgreements?: number;
  /** Percentage of precious metals (KM) */
  preciousMetals?: number;
  /** Percentage of cash and cash equivalents (OSKS) */
  cashAndCashEquivalents?: number;
  /** Percentage of other securities (OSKSYD) */
  otherSecurities?: number;
  /** Percentage of other securities yield (ÖKSYD) */
  otherSecuritiesYield?: number;

  // Treasury and government instruments
  /** Percentage of treasury bills (BPP) */
  treasuryBills?: number;
  /** Percentage of individual investment funds (BYF) */
  individualInvestmentFunds?: number;
  /** Percentage of government debt instruments (DT) */
  governmentDebtInstruments?: number;
  /** Percentage of government debt instruments foreign (DÖT) */
  governmentDebtInstrumentsForeign?: number;
  /** Percentage of eurobonds (EUT) */
  eurobonds?: number;
  /** Percentage of foreign bonds (FB) */
  foreignBonds?: number;
  /** Percentage of foreign currency bonds (FKB) */
  foreignCurrencyBonds?: number;
  /** Percentage of gold and silver (GAS) */
  goldAndSilver?: number;

  // Participation accounts and certificates
  /** Percentage of participation accounts (KBA) */
  participationAccounts?: number;
  /** Percentage of participation accounts gold (KH) */
  participationAccountsGold?: number;
  /** Percentage of participation accounts gold foreign (KHAU) */
  participationAccountsGoldForeign?: number;
  /** Percentage of participation accounts foreign currency (KHD) */
  participationAccountsForeignCurrency?: number;
  /** Percentage of participation accounts Turkish Lira (KHTL) */
  participationAccountsTurkishLira?: number;
  /** Percentage of public lease certificates (KKS) */
  publicLeaseCertificates?: number;
  /** Percentage of public lease certificates foreign currency (KKSD) */
  publicLeaseCertificatesForeignCurrency?: number;
  /** Percentage of public lease certificates Turkish Lira (KKSTL) */
  publicLeaseCertificatesTurkishLira?: number;
  /** Percentage of public lease certificates foreign (KKSYD) */
  publicLeaseCertificatesForeign?: number;
  /** Percentage of participation accounts foreign currency (KİBD) */
  participationAccountsForeignCurrencyBonds?: number;

  // Other instruments
  /** Percentage of other securities Turkish Lira (OST) */
  otherSecuritiesTurkishLira?: number;
  /** Percentage of repo transactions (R) */
  repoTransactions?: number;
  /** Percentage of term deposits (TPP) */
  termDeposits?: number;
  /** Percentage of treasury bills (TR) */
  treasuryBillsShort?: number;
  /** Percentage of variable rate deposits (VDM) */
  variableRateDeposits?: number;
  /** Percentage of money market instruments (VM) */
  moneyMarketInstruments?: number;
  /** Percentage of money market instruments gold (VMAU) */
  moneyMarketInstrumentsGold?: number;
  /** Percentage of money market instruments foreign currency (VMD) */
  moneyMarketInstrumentsForeignCurrency?: number;
  /** Percentage of money market instruments Turkish Lira (VMTL) */
  moneyMarketInstrumentsTurkishLira?: number;
  /** Percentage of variable income instruments (VİNT) */
  variableIncomeInstruments?: number;

  // Investment fund participations
  /** Percentage of investment fund participations (YBA) */
  investmentFundParticipations?: number;
  /** Percentage of investment fund participations bonds (YBKB) */
  investmentFundParticipationsBonds?: number;
  /** Percentage of investment fund participations other securities (YBOSB) */
  investmentFundParticipationsOtherSecurities?: number;
  /** Percentage of investment fund participations foreign currency (YBYF) */
  investmentFundParticipationsForeignCurrency?: number;
  /** Percentage of investment fund participations metals (YMK) */
  investmentFundParticipationsMetals?: number;

  // Precious metals and derivatives
  /** Percentage of precious metals investment funds (KMBYF) */
  preciousMetalsInvestmentFunds?: number;
  /** Percentage of precious metals government debt instruments (KMKBA) */
  preciousMetalsGovernmentDebtInstruments?: number;
  /** Percentage of precious metals public lease certificates (KMKKS) */
  preciousMetalsPublicLeaseCertificates?: number;
}

/**
 * Merged fund data combining history and content information
 *
 * This interface combines all fields from both FundHistoryResponse and FundContentResponse
 * to provide a unified view of fund data. Fields from either source may be null if
 * the corresponding data is not available for that fund/date combination.
 */
export interface FundResponse {
  /** Fund identifier */
  fundCode: string;
  /** Human-readable fund name */
  fundName: string;
  /** Type of fund */
  fundType: FundType;
  /** Date of the data point (YYYY-MM-DD) */
  date: string;

  // History data fields (from FundHistoryResponse)
  /** Fund price/NAV (null if not available) */
  price: number | null;
  /** Trading volume (null if not available) */
  volume: number | null;
  /** Total assets under management (null if not available) */
  assets: number | null;
  /** Number of investors (null if not available) */
  investorCount: number | null;

  // Content data fields (from FundContentResponse)
  /** Total fund value (null if not available) */
  totalValue: number | null;

  // Stock allocations
  /** Percentage of stocks (HS) (null if not available) */
  stocks?: number | null;
  /** Percentage of foreign securities (YYF) (null if not available) */
  foreignSecurities?: number | null;
  /** Percentage of stock-based funds (HB) (null if not available) */
  stockBasedFunds?: number | null;
  /** Percentage of stock-based investment funds (YHS) (null if not available) */
  stockBasedInvestmentFunds?: number | null;

  // Bond allocations
  /** Percentage of bonds (BB) (null if not available) */
  bonds?: number | null;
  /** Percentage of government bonds (DB) (null if not available) */
  governmentBonds?: number | null;
  /** Percentage of private sector bonds (ÖSDB) (null if not available) */
  privateSectorBonds?: number | null;
  /** Percentage of government securities (GSYKB) (null if not available) */
  governmentSecurities?: number | null;
  /** Percentage of government securities yield (GSYY) (null if not available) */
  governmentSecuritiesYield?: number | null;
  /** Percentage of government yield bonds (GYKB) (null if not available) */
  governmentYieldBonds?: number | null;
  /** Percentage of government yield (GYY) (null if not available) */
  governmentYield?: number | null;

  // Deposit and cash allocations
  /** Percentage of deposits (D) (null if not available) */
  deposits?: number | null;
  /** Percentage of reverse purchase agreements (T) (null if not available) */
  reversePurchaseAgreements?: number | null;
  /** Percentage of precious metals (KM) (null if not available) */
  preciousMetals?: number | null;
  /** Percentage of cash and cash equivalents (OSKS) (null if not available) */
  cashAndCashEquivalents?: number | null;
  /** Percentage of other securities (OSKSYD) (null if not available) */
  otherSecurities?: number | null;
  /** Percentage of other securities yield (ÖKSYD) (null if not available) */
  otherSecuritiesYield?: number | null;

  // Treasury and government instruments
  /** Percentage of treasury bills (BPP) (null if not available) */
  treasuryBills?: number | null;
  /** Percentage of individual investment funds (BYF) (null if not available) */
  individualInvestmentFunds?: number | null;
  /** Percentage of government debt instruments (DT) (null if not available) */
  governmentDebtInstruments?: number | null;
  /** Percentage of government debt instruments foreign (DÖT) (null if not available) */
  governmentDebtInstrumentsForeign?: number | null;
  /** Percentage of eurobonds (EUT) (null if not available) */
  eurobonds?: number | null;
  /** Percentage of foreign bonds (FB) (null if not available) */
  foreignBonds?: number | null;
  /** Percentage of foreign currency bonds (FKB) (null if not available) */
  foreignCurrencyBonds?: number | null;
  /** Percentage of gold and silver (GAS) (null if not available) */
  goldAndSilver?: number | null;

  // Participation accounts and certificates
  /** Percentage of participation accounts (KBA) (null if not available) */
  participationAccounts?: number | null;
  /** Percentage of participation accounts gold (KH) (null if not available) */
  participationAccountsGold?: number | null;
  /** Percentage of participation accounts gold foreign (KHAU) (null if not available) */
  participationAccountsGoldForeign?: number | null;
  /** Percentage of participation accounts foreign currency (KHD) (null if not available) */
  participationAccountsForeignCurrency?: number | null;
  /** Percentage of participation accounts Turkish Lira (KHTL) (null if not available) */
  participationAccountsTurkishLira?: number | null;
  /** Percentage of public lease certificates (KKS) (null if not available) */
  publicLeaseCertificates?: number | null;
  /** Percentage of public lease certificates foreign currency (KKSD) (null if not available) */
  publicLeaseCertificatesForeignCurrency?: number | null;
  /** Percentage of public lease certificates Turkish Lira (KKSTL) (null if not available) */
  publicLeaseCertificatesTurkishLira?: number | null;
  /** Percentage of public lease certificates foreign (KKSYD) (null if not available) */
  publicLeaseCertificatesForeign?: number | null;
  /** Percentage of participation accounts foreign currency (KİBD) (null if not available) */
  participationAccountsForeignCurrencyBonds?: number | null;

  // Other instruments
  /** Percentage of other securities Turkish Lira (OST) (null if not available) */
  otherSecuritiesTurkishLira?: number | null;
  /** Percentage of repo transactions (R) (null if not available) */
  repoTransactions?: number | null;
  /** Percentage of term deposits (TPP) (null if not available) */
  termDeposits?: number | null;
  /** Percentage of treasury bills (TR) (null if not available) */
  treasuryBillsShort?: number | null;
  /** Percentage of variable rate deposits (VDM) (null if not available) */
  variableRateDeposits?: number | null;
  /** Percentage of money market instruments (VM) (null if not available) */
  moneyMarketInstruments?: number | null;
  /** Percentage of money market instruments gold (VMAU) (null if not available) */
  moneyMarketInstrumentsGold?: number | null;
  /** Percentage of money market instruments foreign currency (VMD) (null if not available) */
  moneyMarketInstrumentsForeignCurrency?: number | null;
  /** Percentage of money market instruments Turkish Lira (VMTL) (null if not available) */
  moneyMarketInstrumentsTurkishLira?: number | null;
  /** Percentage of variable income instruments (VİNT) (null if not available) */
  variableIncomeInstruments?: number | null;

  // Investment fund participations
  /** Percentage of investment fund participations (YBA) (null if not available) */
  investmentFundParticipations?: number | null;
  /** Percentage of investment fund participations bonds (YBKB) (null if not available) */
  investmentFundParticipationsBonds?: number | null;
  /** Percentage of investment fund participations other securities (YBOSB) (null if not available) */
  investmentFundParticipationsOtherSecurities?: number | null;
  /** Percentage of investment fund participations foreign currency (YBYF) (null if not available) */
  investmentFundParticipationsForeignCurrency?: number | null;
  /** Percentage of investment fund participations metals (YMK) (null if not available) */
  investmentFundParticipationsMetals?: number | null;

  // Precious metals and derivatives
  /** Percentage of precious metals investment funds (KMBYF) (null if not available) */
  preciousMetalsInvestmentFunds?: number | null;
  /** Percentage of precious metals government debt instruments (KMKBA) (null if not available) */
  preciousMetalsGovernmentDebtInstruments?: number | null;
  /** Percentage of precious metals public lease certificates (KMKKS) (null if not available) */
  preciousMetalsPublicLeaseCertificates?: number | null;

  // Fund detail fields (from FundDetail)
  /** Audit firm name (null if not available) */
  auditFirm: string | null;
  /** Email address (null if not available) */
  emailAddress: string | null;
  /** Fund duration (null if not available) */
  fundDuration: string | null;
  /** Contact address (null if not available) */
  contactAddress: string | null;
  /** Founder name (null if not available) */
  founder: string | null;
  /** Founder logo filename (null if not available) */
  founderLogo: string | null;
  /** Umbrella fund type (null if not available) */
  umbrellaFundType: string | null;
  /** Umbrella fund name (null if not available) */
  umbrellaFundName: string | null;
  /** Fund category information (null if not available) */
  fundCategory: FundCategory | null;
  /** Risk level (null if not available) */
  risk: number | null;
  /** Yearly management fee (null if not available) */
  yearlyManagementFee: number | null;
  /** Buying valor (null if not available) */
  buyingValor: number | null;
  /** Selling valor (null if not available) */
  sellingValor: number | null;
  /** Strategy statement (null if not available) */
  strategyStatement: string | null;
  /** Fund benchmarks array */
  fundBenchmarks: FundBenchmark[];
}

/**
 * Raw TEFAS allocation API response structure
 * This represents the actual response format from the TEFAS allocation API
 */
export interface TefasAllocationApiResponse {
  /** Response data array */
  data: TefasAllocationData[];
  /** Response metadata */
  draw?: number;
  recordsTotal?: number;
  recordsFiltered?: number;
}

/**
 * Raw allocation data from TEFAS API
 */
export interface TefasAllocationData {
  /** Date string (timestamp in milliseconds) */
  TARIH: string;
  /** Fund code */
  FONKODU: string;
  /** Fund name */
  FONUNVAN: string;
  /** Total fund value (comma-formatted string) */
  BilFiyat: string;

  // Allocation percentages (all nullable)
  BB?: number;
  BPP?: number;
  BYF?: number;
  D?: number;
  DB?: number;
  DT?: number;
  DÖT?: number;
  EUT?: number;
  FB?: number;
  FKB?: number;
  GAS?: number;
  GSYKB?: number;
  GSYY?: number;
  GYKB?: number;
  GYY?: number;
  HB?: number;
  HS?: number;
  KBA?: number;
  KH?: number;
  KHAU?: number;
  KHD?: number;
  KHTL?: number;
  KKS?: number;
  KKSD?: number;
  KKSTL?: number;
  KKSYD?: number;
  KM?: number;
  KMBYF?: number;
  KMKBA?: number;
  KMKKS?: number;
  KİBD?: number;
  OSKS?: number;
  OSKSYD?: number;
  OST?: number;
  R?: number;
  T?: number;
  TPP?: number;
  TR?: number;
  VDM?: number;
  VM?: number;
  VMAU?: number;
  VMD?: number;
  VMTL?: number;
  VİNT?: number;
  YBA?: number;
  YBKB?: number;
  YBOSB?: number;
  YBYF?: number;
  YHS?: number;
  YMK?: number;
  YYF?: number;
  ÖKSYD?: number;
  ÖSDB?: number;
}
